import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass } from "lucide-react";
import client from "../lib/api";
import { PHOTOS, HERO_HEADLINE, HERO_SUPPORTING, TRUST } from "../lib/data";
import { SectionTitle, FocusCard, Reveal, Eyebrow } from "../components/site/common";

export default function Home() {
  const [activities, setActivities] = useState([]);
  useEffect(() => {
  client
    .get("/public/activities")
    .then(({ data }) => {
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.activities)
          ? data.activities
          : [];

      setActivities(list);
    })
    .catch((error) => {
      console.error("Failed to load activities:", error);
      setActivities([]);
    });
}, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-ivory">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 lg:grid-cols-12 lg:gap-8 lg:px-10 lg:py-24">
          <div className="lg:col-span-6">
            <Eyebrow className="animate-fade-in">{TRUST.name} · Est. {TRUST.established}</Eyebrow>
            <h1 className="mt-5 text-4xl font-medium leading-[1.05] tracking-tight text-maroon sm:text-5xl lg:text-6xl animate-fade-up">
              {HERO_HEADLINE}
            </h1>
            <p className="mt-4 font-serif text-xl italic text-gold-dark">{TRUST.tagline}</p>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-ink-muted lg:text-lg">{HERO_SUPPORTING}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/support" data-testid="hero-support-btn" className="inline-flex items-center gap-2 bg-maroon px-7 py-4 text-xs uppercase tracking-wide text-ivory transition-colors duration-300 hover:bg-maroon-dark">
                Support Our Mission <ArrowRight size={16} />
              </Link>
              <Link to="/activities" data-testid="hero-explore-btn" className="inline-flex items-center gap-2 border border-maroon px-7 py-4 text-xs uppercase tracking-wide text-maroon transition-colors duration-300 hover:bg-maroon/5">
                Explore Our Activities
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative">
              <div className="aspect-[4/3] overflow-hidden border border-gold/30">
                <img src={PHOTOS.heroTemple} alt="Heritage temple architecture" className="h-full w-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden border border-gold/40 bg-white px-6 py-5 shadow-[0_4px_24px_rgba(88,24,31,0.06)] sm:block">
                <div className="font-serif text-3xl text-maroon">Since 1995</div>
                <div className="text-xs tracking-wide text-ink-muted">Committed to service, faith &amp; community</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Indicators */}
      <section className="border-y border-gold/20 bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-gold/15 px-5 lg:grid-cols-4 lg:px-10">
          {[["1995", "Established"], ["Tamil Nadu", "Area of Focus"], ["Religious", "Primary Sector"], ["Faith + Service", "Guiding Values"]].map(([a, b]) => (
            <div key={b} className="px-4 py-8 text-center">
              <div className="font-serif text-2xl text-maroon lg:text-3xl">{a}</div>
              <div className="mt-1 text-xs uppercase tracking-[0.15em] text-ink-muted">{b}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Intro */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionTitle eyebrow="Who We Are" title="An Indian Trust rooted in faith, service and community." />
          </div>
          <div className="lg:col-span-7">
            <p className="text-base leading-relaxed text-ink-muted lg:text-lg">
              Sri Akilandeswari Amman Trust was established in 1995 with a commitment to serving society through compassion, community responsibility and meaningful social initiatives. The Trust's primary area of focus is religious and spiritual service connected with temples, together with cultural values and socially beneficial initiatives.
            </p>
            <Link to="/about" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-maroon hover:text-gold-dark">
              Read our story <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* Focus areas */}
      <section className="bg-ivory-dark/40">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
          <SectionTitle eyebrow="Our Areas of Focus" title="Where our service is directed" intro="Nine focus areas guide the Trust's work — with religious and temple-related service as our primary sector." />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {activities.map((a, i) => (
              <FocusCard key={a.id} item={a} index={i} highlight={i === 0} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-maroon">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-5 py-16 lg:flex-row lg:items-center lg:justify-between lg:px-10 lg:py-20">
          <div>
            <Compass className="text-gold-light" size={30} strokeWidth={1.3} />
            <h2 className="mt-4 max-w-xl text-3xl font-medium text-ivory lg:text-4xl">Join us in the spirit of service and compassion.</h2>
          </div>
          <Link to="/support" className="inline-flex items-center gap-2 border border-gold-light bg-transparent px-7 py-4 text-xs uppercase tracking-wide text-ivory transition-colors duration-300 hover:bg-ivory/10">
            Register Your Interest <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
